import React, { useState, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { AppBaseDropdown } from '../AppBaseDropdown';
import { AppInput } from '../AppInput';
import type { AppSingleSelectProps, SelectOption } from './AppSingleSelect.types';
import { ChevronDownIcon } from './ChevronDownIcon';
import { usePlatform } from '@pro-sto/shared/hooks';
import './AppSingleSelect.css';

/**
 * AppSingleSelect - Компонент для выбора одного значения из списка
 * 
 * @example
 * ```tsx
 * const options = [
 *   { label: 'Москва', value: 'moscow' },
 *   { label: 'Санкт-Петербург', value: 'spb' },
 * ];
 * 
 * <AppSingleSelect
 *   label="Выберите город"
 *   options={options}
 *   value={selectedCity}
 *   onChange={setSelectedCity}
 *   clearable
 * />
 * ```
 */
export const AppSingleSelect: React.FC<AppSingleSelectProps> = ({
  value,
  label,
  disabled = false,
  error,
  required = false,
  placeholder = 'Выберите...',
  background = 'default',
  searchPlaceholder = 'Поиск...',
  clearable = false,
  options = [],
  onChange,
  toggle,
  dropdown,
  option,
  baseDropdownProps = {},
  className,
  'data-testid': dataTestId,
}) => {
  const platform = usePlatform();
  const isMobileMode = platform === 'mobile';
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // В mobile режиме: фиксированная высота если есть поиск (>10 опций), иначе динамическая
  const hasSearch = options.length > 10;

  // Фильтрация опций по поисковому запросу
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    return options.filter(opt =>
      opt.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  // Обработчик выбора опции
  const handleSelect = useCallback((selectedOption: SelectOption) => {
    onChange?.(selectedOption);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange]);

  // Обработчик очистки значения
  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
  }, [onChange]);

  // Обработчик клика по input
  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }, [disabled, isOpen]);

  // Обработчик закрытия dropdown
  const handleDropdownClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  // Рендер toggle (кастомный или дефолтный)
  const renderToggle = () => {
    if (toggle) return toggle;

    const arrowClassName = classNames('app-single-select__arrow', {
      'app-single-select__arrow_open': isOpen,
    });

    return (
      <div className="app-single-select__input-container">
        <div className="app-single-select__input-wrapper" onClick={handleInputClick}>
          <AppInput
            value={value?.label ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            error={error}
            background={background}
            readOnly
            inputProps={{ style: { cursor: 'pointer' } }}
          />
          {clearable && value && !disabled && (
            <button
              className="app-single-select__clear-button"
              onClick={handleClear}
              type="button"
              aria-label="Очистить"
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
            >
              ×
            </button>
          )}
          <div className={arrowClassName} aria-hidden="true">
            <ChevronDownIcon color="#B2B1AE" size={20} />
          </div>
        </div>
      </div>
    );
  };

  // Рендер dropdown (кастомный или дефолтный)
  const renderDropdown = () => {
    if (dropdown) return dropdown;

    return (
      <div className="app-single-select__dropdown" role="listbox" data-testid={dataTestId ? `${dataTestId}-dropdown` : undefined}>
        {/* Поиск показывается только для списков > 10 элементов */}
        {options.length > 10 && (
          <input
            type="text"
            className="app-single-select__search"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            aria-label="Поиск по опциям"
          />
        )}
        <div className="app-single-select__options-container">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((optionItem) => {
              const optionClassName = classNames('app-single-select__option', {
                'app-single-select__option_selected': value?.value === optionItem.value,
              });

              return (
                <div
                  key={optionItem.value}
                  className={optionClassName}
                  onClick={() => handleSelect(optionItem)}
                  role="option"
                  aria-selected={value?.value === optionItem.value}
                  title={optionItem.label}
                  data-testid={dataTestId ? `${dataTestId}-option-${optionItem.value}` : undefined}
                >
                  <span className="app-single-select__option-label">{option || optionItem.label}</span>
                  {value?.value === optionItem.value && (
                    <div className="app-single-select__option-check" aria-hidden="true">
                      ✓
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="app-single-select__no-options" role="status">
              Ничего не найдено
            </div>
          )}
        </div>
      </div>
    );
  };

  // Рендер mobile drawer содержимого
  const renderMobileDrawer = () => {
    return (
      <div className="app-single-select__mobile-drawer">
        {/* Поиск показывается только для списков > 10 элементов */}
        {hasSearch && (
          <div className="app-single-select__mobile-search">
            <AppInput
              value={searchQuery}
              placeholder={searchPlaceholder}
              onChange={setSearchQuery}
              autoComplete="off"
              autoFocus
            />
          </div>
        )}
        
        <div className="app-single-select__mobile-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((optionItem) => {
              const optionClassName = classNames('app-single-select__mobile-option', {
                'app-single-select__mobile-option_selected': value?.value === optionItem.value,
              });

              return (
                <div
                  key={optionItem.value}
                  className={optionClassName}
                  title={optionItem.label}
                  onClick={() => handleSelect(optionItem)}
                >
                  {option || optionItem.label}
                  {value?.value === optionItem.value && (
                    <div className="app-single-select__mobile-option-check" aria-hidden="true">
                      ✓
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="app-single-select__mobile-no-options">
              Ничего не найдено
            </div>
          )}
        </div>
      </div>
    );
  };

  const wrapperClassName = classNames('app-single-select', {
    'app-single-select_disabled': disabled,
    'app-single-select_error': !!error,
  }, className);

  const mobileDrawerProps = isMobileMode && hasSearch ? {
    mobileDrawerMaxHeight: '66.67vh',
    mobileDrawerFixedHeight: true,
  } : {};

  return (
    <div className={wrapperClassName} data-testid={dataTestId}>
      {label && (
        <label className="app-single-select__label" data-testid={dataTestId ? `${dataTestId}-label` : undefined}>
          {label}
          {required && <span className="app-single-select__required">*</span>}
        </label>
      )}
      <AppBaseDropdown
        {...baseDropdownProps}
        {...mobileDrawerProps}
        opened={isOpen}
        onClose={handleDropdownClose}
        toggle={renderToggle()}
        dropdown={isMobileMode ? renderMobileDrawer() : renderDropdown()}
        maxDropdownHeight={280}
        noRestrictHeigth={true}
      />
    </div>
  );
};

AppSingleSelect.displayName = 'AppSingleSelect';